/**
 * TencentBucketSelect — the shared Tencent COS bucket-picker control used by
 * the SharedLocationsCard connected-row bucket editor AND the pending-claim
 * approve panel. Extracted so both surfaces render the exact same three
 * states without duplicating ~60 lines:
 *
 *   1. Loading      — spinner row ("Loading buckets…").
 *   2. Free-text    — `data.supported === false` (missing cos:ListBuckets
 *                     scope, no creds, or a network error). Mirrors the legacy
 *                     bucket field so the operator is never blocked; validated
 *                     inline against the same regex the backend zod + DB CHECK
 *                     use.
 *   3. Dropdown     — `data.supported === true`. Lists buckets with their
 *                     region, plus a Tencent-console link when the account is
 *                     empty.
 *
 * Presentational only: the parent owns `value`, validity (`invalid`), and any
 * surrounding label / helper microcopy / footer buttons, so each site can
 * frame the picker differently (an editor with Save/Clear vs. a selector that
 * re-drives the FolderPicker).
 */

import { ExternalLink, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { RouterOutputs } from '@/lib/trpc';

export type TencentBucketsData = RouterOutputs['printAgent']['listTencentBuckets'];

/**
 * Tencent COS bucket-name validator. Mirrors the backend zod
 * (apps/backend/src/routers/locations.ts `tencentBucketNameValidator`) and the
 * DB CHECK on `location_client_organizations.tencent_bucket_name`. Exported so
 * every caller validates identically before firing a mutation.
 */
export const TENCENT_BUCKET_NAME_RE = /^[a-z0-9][a-z0-9-]*-[0-9]{10,}$/;

interface TencentBucketSelectProps {
  isLoading: boolean;
  data: TencentBucketsData | undefined;
  value: string;
  onChange: (value: string) => void;
  /**
   * Non-empty free-text value that fails `TENCENT_BUCKET_NAME_RE`. Only styled
   * in the free-text branch (dropdown values are always well-formed).
   */
  invalid?: boolean;
  disabled?: boolean;
}

export function TencentBucketSelect({
  isLoading,
  data,
  value,
  onChange,
  invalid = false,
  disabled = false,
}: TencentBucketSelectProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading buckets…
      </div>
    );
  }

  // Graceful fallback when cos:ListBuckets is unavailable (missing scope,
  // network error, or no credentials). Free-text input mirrors the legacy
  // bucket field so the operator is never blocked. We DO validate inline
  // against the same regex the backend zod + DB CHECK use, so a typo doesn't
  // round-trip through a mutation just to fail.
  if (data?.supported === false) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {data.reason === 'no_credentials'
            ? 'Tencent COS credentials are not configured for this organisation. Configure them in Production Settings → Advanced → Tencent Cloud Storage.'
            : data.reason === 'missing_scope'
              ? "We couldn't list your Tencent buckets automatically (the credentials don't have permission to list). Type the bucket name below."
              : "We couldn't load your Tencent buckets right now. Type the bucket name below."}
        </p>
        <input
          type="text"
          placeholder="mz-prints-1300000000"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-9 w-full rounded-md border bg-background px-3 font-mono text-xs ${
            invalid ? 'border-destructive' : ''
          }`}
          aria-invalid={invalid}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
        />
        <p className={`text-[10px] ${invalid ? 'text-destructive' : 'text-muted-foreground/90'}`}>
          {invalid ? (
            'Must be <name>-<appid>: lowercase letters, digits, hyphens; APPID is 10 or more digits.'
          ) : (
            <>
              Format: <code>&lt;name&gt;-&lt;appid&gt;</code> (e.g. <code>mz-prints-1300000000</code>).
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9 text-xs">
        <SelectValue placeholder="Pick a bucket" />
      </SelectTrigger>
      <SelectContent>
        {(data?.buckets ?? []).map((b) => (
          <SelectItem key={b.name} value={b.name}>
            <span className="font-mono">{b.name}</span>
            {b.region && <span className="text-muted-foreground"> · {b.region}</span>}
          </SelectItem>
        ))}
        {(data?.buckets ?? []).length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center space-y-1.5">
            <p>No buckets found in your Tencent COS account.</p>
            <a
              href="https://console.cloud.tencent.com/cos/bucket"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              Open Tencent COS console
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
